class ProjectSpotlightTable extends HTMLElement {
  connectedCallback() {
    this.rows = Array.from(this.querySelectorAll('[data-spotlight-row]'));
    this.checkboxes = Array.from(this.querySelectorAll('[data-spotlight-filter] .checkbox__input'));
    this.emptyState = this.querySelector('[data-spotlight-empty]');
    this.grid = this.querySelector('[data-spotlight-grid]');
    this.resultCount = this.querySelector('[data-spotlight-count]');
    this.clearButton = this.querySelector('[data-spotlight-clear]');
    this.mobileToggle = this.querySelector('[data-spotlight-mobile-toggle]');
    this.filtersPanel = this.querySelector('[data-spotlight-filters]');
    this.sortButtons = Array.from(this.querySelectorAll('[data-spotlight-sort]'));
    this.groupToggles = Array.from(this.querySelectorAll('[data-spotlight-group-toggle]'));

    this.sortKey = 'ps';
    this.sortDir = 'asc';

    this.onFilterChange = this.onFilterChange.bind(this);
    this.onClear = this.onClear.bind(this);
    this.onMobileToggle = this.onMobileToggle.bind(this);
    this.onSortClick = this.onSortClick.bind(this);
    this.onGroupToggle = this.onGroupToggle.bind(this);

    this.checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.onFilterChange);
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', this.onClear);
    }

    if (this.mobileToggle && this.filtersPanel) {
      this.mobileToggle.addEventListener('click', this.onMobileToggle);
    }

    this.sortButtons.forEach((button) => {
      button.addEventListener('click', this.onSortClick);
    });

    this.groupToggles.forEach((button) => {
      button.addEventListener('click', this.onGroupToggle);
    });

    this.applyFiltersFromUrl();
    this.applyFilters();
    this.updateSortUi();
  }

  disconnectedCallback() {
    this.checkboxes.forEach((checkbox) => {
      checkbox.removeEventListener('change', this.onFilterChange);
    });

    if (this.clearButton) {
      this.clearButton.removeEventListener('click', this.onClear);
    }

    if (this.mobileToggle) {
      this.mobileToggle.removeEventListener('click', this.onMobileToggle);
    }

    this.sortButtons.forEach((button) => {
      button.removeEventListener('click', this.onSortClick);
    });

    this.groupToggles.forEach((button) => {
      button.removeEventListener('click', this.onGroupToggle);
    });
  }

  applyFiltersFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const groups = ['industry', 'application', 'location', 'products'];

    groups.forEach((group) => {
      const values = params
        .getAll(group)
        .flatMap((value) => value.split(','))
        .map((value) => value.trim().toLowerCase())
        .filter(Boolean);

      if (values.length === 0) return;

      this.checkboxes
        .filter((checkbox) => checkbox.dataset.filterGroup === group)
        .forEach((checkbox) => {
          if (values.includes(checkbox.value.toLowerCase())) {
            checkbox.checked = true;
          }
        });
    });
  }

  onMobileToggle() {
    const expanded = this.mobileToggle.getAttribute('aria-expanded') === 'true';
    this.mobileToggle.setAttribute('aria-expanded', String(!expanded));
    this.filtersPanel.classList.toggle('is-open', !expanded);
  }

  onGroupToggle(event) {
    const button = event.currentTarget;
    const group = button.closest('[data-spotlight-filter-group]');
    if (!group) return;

    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    group.classList.toggle('is-collapsed', expanded);
  }

  onClear() {
    this.checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.applyFilters();
  }

  onFilterChange() {
    this.applyFilters();
  }

  onSortClick(event) {
    const button = event.currentTarget;
    const key = button.dataset.spotlightSort;

    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }

    this.sortRows();
    this.updateSortUi();
  }

  getActiveFilters() {
    return {
      industry: this.checkboxes
        .filter((checkbox) => checkbox.dataset.filterGroup === 'industry' && checkbox.checked)
        .map((checkbox) => checkbox.value),
      application: this.checkboxes
        .filter((checkbox) => checkbox.dataset.filterGroup === 'application' && checkbox.checked)
        .map((checkbox) => checkbox.value),
      location: this.checkboxes
        .filter((checkbox) => checkbox.dataset.filterGroup === 'location' && checkbox.checked)
        .map((checkbox) => checkbox.value),
      products: this.checkboxes
        .filter((checkbox) => checkbox.dataset.filterGroup === 'products' && checkbox.checked)
        .map((checkbox) => checkbox.value),
    };
  }

  rowMatches(row, filters) {
    if (filters.industry.length > 0 && !filters.industry.includes(row.dataset.industry)) {
      return false;
    }

    if (filters.application.length > 0 && !filters.application.includes(row.dataset.application)) {
      return false;
    }

    if (filters.location.length > 0 && !filters.location.includes(row.dataset.location)) {
      return false;
    }

    if (filters.products.length > 0) {
      const rowProducts = (row.dataset.products || '')
        .split('|')
        .map((value) => value.trim())
        .filter(Boolean);
      const hasProduct = filters.products.some((product) => rowProducts.includes(product));
      if (!hasProduct) return false;
    }

    return true;
  }

  applyFilters() {
    const filters = this.getActiveFilters();
    const hasActiveFilters =
      filters.industry.length > 0 ||
      filters.application.length > 0 ||
      filters.location.length > 0 ||
      filters.products.length > 0;

    let visibleCount = 0;

    this.rows.forEach((row) => {
      const match = this.rowMatches(row, filters);
      row.hidden = !match;
      if (match) visibleCount += 1;
    });

    if (this.emptyState) {
      this.emptyState.hidden = visibleCount > 0;
    }

    if (this.grid) {
      this.grid.hidden = visibleCount === 0;
    }

    if (this.resultCount) {
      this.resultCount.textContent = String(visibleCount);
    }

    if (this.clearButton) {
      this.clearButton.hidden = !hasActiveFilters;
    }

    this.updateFilterCounts(filters);
    this.sortRows();
  }

  optionCount(checkbox, filters) {
    const group = checkbox.dataset.filterGroup;
    const value = checkbox.value;
    const filtersWithoutGroup = { ...filters, [group]: [] };
    let count = 0;

    this.rows.forEach((row) => {
      if (!this.rowMatches(row, filtersWithoutGroup)) return;

      if (group === 'products') {
        const rowProducts = (row.dataset.products || '')
          .split('|')
          .map((item) => item.trim())
          .filter(Boolean);
        if (rowProducts.includes(value)) count += 1;
        return;
      }

      if (row.dataset[group] === value) count += 1;
    });

    return count;
  }

  updateFilterCounts(filters) {
    this.checkboxes.forEach((checkbox) => {
      const countElement = checkbox.parentElement?.querySelector('[data-spotlight-filter-count]');
      if (!countElement) return;

      const count = this.optionCount(checkbox, filters);
      countElement.textContent = `(${count})`;
    });
  }

  sortRows() {
    const tbody = this.querySelector('[data-spotlight-tbody]');
    if (!tbody) return;

    const visibleRows = this.rows.filter((row) => !row.hidden);
    const multiplier = this.sortDir === 'asc' ? 1 : -1;

    visibleRows.sort((a, b) => {
      const aValue = (a.dataset[this.sortKey] || '').toLowerCase();
      const bValue = (b.dataset[this.sortKey] || '').toLowerCase();
      return aValue.localeCompare(bValue, undefined, { numeric: true }) * multiplier;
    });

    visibleRows.forEach((row) => tbody.appendChild(row));
  }

  updateSortUi() {
    this.sortButtons.forEach((button) => {
      const key = button.dataset.spotlightSort;
      const isActive = key === this.sortKey;
      button.setAttribute('aria-sort', isActive ? (this.sortDir === 'asc' ? 'ascending' : 'descending') : 'none');
      button.classList.toggle('is-active', isActive);
      button.classList.toggle('is-asc', isActive && this.sortDir === 'asc');
      button.classList.toggle('is-desc', isActive && this.sortDir === 'desc');
    });
  }
}

if (!customElements.get('project-spotlight-table')) {
  customElements.define('project-spotlight-table', ProjectSpotlightTable);
}
